import React, { useMemo, useState } from 'react';
import { useQuery } from '@apollo/client';

import { useSeason } from '../SeasonState';
import {
  GetVendorsWithInvoicePreview,
  GetVendorsWithInvoicePreviewVariables,
  GET_VENDORS_WITH_INVOICE_PREVIEW,
} from '../../queries/billing';
import {
  LoadingIndicator,
  ApolloError,
  HStack,
  VStack,
  SearchInput,
  Text,
  Spacer,
} from '../../core-ui';

import { FilterDropdown } from '../Vendors/FilterDropdown';
import { filterVendors } from '../Vendors/filterVendors';
import { APP_STATUSES } from '../../support/constants/AppStatuses';
import { BillingVendorTable } from './BillingVendorTable';
import { getDefaultVendorStatusSelection } from '../Vendors/getDefaultVendorStatusSelection';
import { getCategoryOptionsFromGraphQLFields } from '../../support/helpers/CategoryOptions';
import { toMap } from '../../support/helpers/toMap';
import { AppStatus } from '../../core/constants/AppStatus';

type Props = {
  selectedMarketDates: Set<string>;
  selectedVendors: Set<string>;
  setSelectedVendors: (vendors: Set<string>) => void;
  signupFeeOnly: boolean;
};

export function VendorSelectionPanel(props: Props) {
  const {
    selectedMarketDates,
    selectedVendors,
    setSelectedVendors,
    signupFeeOnly,
  } = props;

  const { season } = useSeason();

  const [searchText, setSearchText] = useState('');
  const [selectedCategories, setSelectedCategories] = useState(
    new Set<string>(),
  );
  const [selectedStatuses, setSelectedStatuses] = useState(
    new Set<string>([AppStatus.PENDING, AppStatus.APPROVED, AppStatus.ACTIVE]),
  );
  const [selectedTags, setSelectedTags] = useState(new Set<string>());

  const { data, error } = useQuery<
    GetVendorsWithInvoicePreview,
    GetVendorsWithInvoicePreviewVariables
  >(GET_VENDORS_WITH_INVOICE_PREVIEW, {
    variables: {
      seasonId: season?.id ?? '',
      marketDatesIn: Array.from(selectedMarketDates),
      includeSalesData: false,
    },
    onCompleted: (_data) => {
      // TODO: setSelectedVendors with filtered selection
    },
    fetchPolicy: 'cache-and-network',
  });

  const currency = data?.myAccount.settings.currency ?? 'USD';
  const rawVendors = data?.season?.vendors ?? [];
  const vendors = signupFeeOnly
    ? rawVendors
    : rawVendors.filter((vendor) =>
        vendor.markets.some((market) => market.dates.length > 0),
      );
  const totalVendors = rawVendors.length;
  const options = getCategoryOptionsFromGraphQLFields(
    data?.season?.signupForm?.fields ?? [],
  );
  const optionsMap = toMap(options);

  const filteredVendors = useMemo(() => {
    return filterVendors({
      vendors,
      filters: {
        searchText,
        selectedCategories,
        selectedStatuses,
        selectedTags,
      },
      optionsMap,
    });
  }, [
    optionsMap,
    searchText,
    selectedCategories,
    selectedStatuses,
    selectedTags,
    vendors,
  ]);

  const tags = data?.season?.tags ?? [];
  const vendorCategories = data?.season?.vendorCategories ?? [];

  if (error) {
    return <ApolloError error={error} />;
  }

  if (!data) {
    return <LoadingIndicator />;
  }

  if (!data.season) {
    return <p>{t('Season not found')}</p>;
  }

  return (
    <VStack flex={1} minH={0} alignItems="stretch" pt={4} spacing={2}>
      <HStack>
        <SearchInput
          value={searchText}
          onChange={(event) => {
            setSearchText(event.target.value);
          }}
          onClear={() => {
            setSearchText('');
          }}
        />
        <FilterDropdown
          categories={vendorCategories}
          selectedCategories={selectedCategories}
          statuses={APP_STATUSES}
          selectedStatuses={selectedStatuses}
          tags={tags}
          selectedTags={selectedTags}
          onApply={({ categories, statuses, tags }) => {
            setSelectedCategories(categories);
            setSelectedStatuses(statuses);
            setSelectedTags(tags);
          }}
          onReset={() => {
            setSelectedCategories(new Set());
            setSelectedStatuses(getDefaultVendorStatusSelection());
            setSelectedTags(new Set());
          }}
        />
        {filteredVendors.length === totalVendors ? null : (
          <Text>
            {t('Showing {showing} of {total} vendor(s)', {
              showing: filteredVendors.length,
              total: totalVendors,
            })}
          </Text>
        )}
        <Spacer />
        {selectedVendors.size === 0 ? null : (
          <Text>
            {t('{count} vendor(s) selected', { count: selectedVendors.size })}
          </Text>
        )}
      </HStack>
      <BillingVendorTable
        currency={currency}
        optionsMap={optionsMap}
        vendors={filteredVendors}
        selected={selectedVendors}
        onSelectionChange={(selected) => setSelectedVendors(selected)}
      />
    </VStack>
  );
}
